"use client";

import { useCallback, useEffect, useId, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { Sheet, SheetFooter } from "@/components/ui/sheet";
import { Button, SubmitButton } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { showToast } from "@/components/ui/sonner";
import { customInstance } from "@/lib/api/client";
import { TalentSearchFiltersProvider } from "@/components/search/talent-search-filters-provider";
import type { SearchFilters } from "@/lib/api/types";
import type { StageItem } from "@/features/jobs/components/stage-cards";
import { FOCUS_RING } from "../icon-button";
import { PresetSheetHeader } from "../preset-sheet-header";
import { PresetFormFields } from "../preset-form-fields";
import {
  buildDefaultValues,
  presetFormSchema,
  serializePresetEntities,
  type EntityItem,
  type PresetFormValues,
} from "../schema";
import { getPresetMeta, type PresetTypeSlug } from "../../lib/categories";
import {
  serializeStagePresetItems,
  stagesToPresetFormStages,
} from "../../lib/stage-preset";

const EMPTY_FILTERS: SearchFilters = {};

export interface CreatePresetProps {
  open: boolean;
  onClose: () => void;
  presetType?: PresetTypeSlug;
  initialFilters?: SearchFilters;
  initialStages?: StageItem[];
  initialEntities?: EntityItem[];
}

export function CreatePreset({
  open,
  onClose,
  presetType: initialPresetType,
  initialFilters = EMPTY_FILTERS,
  initialStages,
  initialEntities,
}: CreatePresetProps) {
  const queryClient = useQueryClient();

  const form = useForm<PresetFormValues>({
    resolver: zodResolver(presetFormSchema),
    mode: "onChange",
    defaultValues: buildDefaultValues(initialPresetType ?? "companies", {
      entities: initialEntities,
      stages: initialStages
        ? stagesToPresetFormStages(initialStages)
        : undefined,
    }),
  });

  const presetType = useWatch({ control: form.control, name: "presetType" });
  const isFilterType = presetType === "filters";
  const [draftState, setDraftState] = useState<{
    resetKey: string;
    filters: SearchFilters;
  } | null>(null);
  const [draftOverride, setDraftOverride] = useState<{
    version: number;
    filters: SearchFilters;
  } | null>(null);
  const draftSeedFilters = draftOverride?.filters ?? initialFilters;
  const draftResetKey = `${open ? "open" : "closed"}:${presetType}:${draftOverride?.version ?? 0}:${initialStages?.length ?? 0}:${initialEntities?.length ?? 0}`;

  useEffect(() => {
    if (!open) return;
    form.reset(
      buildDefaultValues(initialPresetType ?? "companies", {
        entities: initialEntities,
        stages: initialStages
          ? stagesToPresetFormStages(initialStages)
          : undefined,
      }),
    );
  }, [open, initialPresetType, initialStages, initialEntities, form]);

  const handleDraftFiltersChange = useCallback(
    (nextFilters: SearchFilters) => {
      setDraftState({
        resetKey: draftResetKey,
        filters: nextFilters,
      });
    },
    [draftResetKey],
  );

  const handleClose = useCallback(() => {
    setDraftState(null);
    setDraftOverride(null);
    onClose();
  }, [onClose]);

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      customInstance("/api/presets", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["presets"] });
      showToast.success("Preset created");
      handleClose();
    },
    onError: () => {
      showToast.error("Failed to create preset");
    },
  });

  const handleReset = useCallback(() => {
    form.reset(
      buildDefaultValues(presetType, {
        entities: initialEntities,
        stages: initialStages
          ? stagesToPresetFormStages(initialStages)
          : undefined,
      }),
    );
    if (isFilterType) {
      setDraftState(null);
      setDraftOverride((current) => ({
        version: (current?.version ?? 0) + 1,
        filters: EMPTY_FILTERS,
      }));
    }
  }, [form, presetType, isFilterType, initialStages, initialEntities]);

  const onValidSubmit = useCallback(
    (values: PresetFormValues) => {
      const typeMeta = getPresetMeta(values.presetType);
      const payload: Record<string, unknown> = {
        preset_type: typeMeta.apiType,
        title: values.title,
        description: values.description || undefined,
        visibility: values.visibility,
        items:
          values.presetType === "filters" || values.presetType === "job-stages"
            ? []
            : serializePresetEntities(values.entities),
      };

      if (values.presetType === "filters") {
        const filters =
          draftState?.resetKey === draftResetKey
            ? draftState.filters
            : draftSeedFilters;
        payload.filters_json = JSON.stringify(filters);
      }

      if (values.presetType === "job-stages") {
        payload.stages_json = JSON.stringify(
          serializeStagePresetItems(values.stages),
        );
      }

      createMutation.mutate(payload);
    },
    [draftResetKey, draftSeedFilters, draftState, createMutation],
  );

  const isPending = createMutation.isPending;
  const canSubmit = form.formState.isValid;
  const submitHelpId = useId();

  return (
    <Sheet
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) handleClose();
      }}
      size="md"
      disableFocusTrap
    >
      <PresetSheetHeader title="Create Preset" />
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onValidSubmit)}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="flex min-h-0 flex-1 flex-col gap-5 bg-background dark:bg-surface-elevated px-4 py-4 sm:px-6 sm:py-6 overflow-y-auto">
            {isFilterType ? (
              <TalentSearchFiltersProvider
                initialFilters={draftSeedFilters}
                onFiltersChange={handleDraftFiltersChange}
                resetKey={draftResetKey}
              >
                <PresetFormFields form={form} isEdit={false} />
              </TalentSearchFiltersProvider>
            ) : (
              <PresetFormFields form={form} isEdit={false} />
            )}
          </div>

          <SheetFooter className="justify-between">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                aria-label="Clear all preset fields"
                onClick={handleReset}
                icon={
                  <Icon
                    name="IconBroomSparkle"
                    size={16}
                    aria-hidden="true"
                    className="text-heading opacity-60"
                  />
                }
                className={`relative before:absolute before:left-1/2 before:top-1/2 before:h-11 before:w-full before:min-w-11 before:-translate-x-1/2 before:-translate-y-1/2 before:content-[''] ${FOCUS_RING}`}
              >
                <span className="px-1">Clear</span>
              </Button>
            </div>
            <div className="flex flex-col items-end gap-1">
              <SubmitButton
                type="submit"
                variant="primary"
                size="sm"
                disabled={!canSubmit}
                loading={isPending}
                loadingLabel="Creating"
                aria-describedby={!canSubmit ? submitHelpId : undefined}
              >
                Create
              </SubmitButton>
            </div>
          </SheetFooter>
        </form>
      </Form>
    </Sheet>
  );
}
